import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Form, Button, FormGroup, Label, Input } from 'reactstrap';
import { useFormik } from 'formik';
import { createOrUpdateStore } from '../../../actions/organization';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import { useDispatch, useSelector } from 'react-redux';
import InnerLoader from '../../../Components/Common/InnerLoader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CreateStoreForm = ({ store = null, handleClose = () => {} }) => {
  const dispatch = useDispatch();
  const { countries, organizations, organizationTree } = useSelector(
    (state) => state?.organization
  );

  const { brands } = organizationTree;

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      organizationId: '',
      brandId: '',
      countryCode: '',
      area: '',
      address: '',
      isDisabled: false,
      isEcommerce: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      organizationId: Yup.string().required('Organization is required'),
      brandId: Yup.string().required('Brand is required'),
      countryCode: Yup.string().required('Please select a country'),
      area: Yup.string().required('Area is required'),
      address: Yup.string().required('Address is required'),
      isDisabled: Yup.boolean(),
      isEcommerce: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const inputData = {
          input: {
            ...values,
          },
        };

        const res = await createOrUpdateStore(inputData, dispatch);
        if (res?.storeId) {
          if (values?.storeId) {
            alertSuccess('Store updated successfully');
          } else {
            alertSuccess('Store created successfully');
          }
          setTimeout(() => {
            resetFormValues();
            handleClose();
          }, 1000);
        } else {
          alertError('Failed, Please try again later');
        }
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (e) {
        setLoading(false);
        console.log(e);
      }
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    resetForm,
    setValues,
  } = formik;

  const resetFormValues = () => {
    resetForm({
      values: {
        name: '',
        organizationId: '',
        brandId: '',
        countryCode: '',
        area: '',
        address: '',
        isDisabled: false,
        isEcommerce: false,
      },
    });
  };

  useEffect(() => {
    setValues({
      name: store?.name || '',
      organizationId: store?.organizationId || '',
      brandId: store?.brandId || '',
      storeId: store?.storeId || '',
      countryCode: store?.countryCode,
      area: store?.area || '',
      address: store?.address || '',
      isDisabled: store?.isDisabled || false,
      isDeleted: store?.isDeleted,
      isEcommerce: store?.isEcommerce || false,
    });
  }, [store]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit}>
        <FormGroup className="mb-3">
          <Label for="name">Name</Label>
          <Input
            type="text"
            name="name"
            value={values?.name}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.name && !!errors?.name}
          />
          {touched?.name && errors?.name && (
            <div className="invalid-feedback">{errors?.name}</div>
          )}
        </FormGroup>

        <FormGroup className="mb-3">
          <Label for="organizationId">Organization</Label>
          <Input
            type="select"
            name="organizationId"
            value={values?.organizationId}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.organizationId && !!errors?.organizationId}
          >
            <option value="">Select Organization</option>
            {organizations?.map((org, index) => (
              <option key={index} value={org?.organizationId}>
                {org?.name}
              </option>
            ))}
          </Input>
          {touched?.organizationId && errors?.organizationId && (
            <div className="invalid-feedback">{errors?.organizationId}</div>
          )}
        </FormGroup>

        <FormGroup className="mb-3">
          <Label for="brandId">Brand</Label>
          <Input
            type="select"
            name="brandId"
            value={values?.brandId}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.brandId && !!errors?.brandId}
          >
            <option value="">Select Brand</option>
            {brands?.map((brand, index) => (
              <option key={index} value={brand?.brandId}>
                {brand?.name}
              </option>
            ))}
          </Input>
          {touched?.brandId && errors?.brandId && (
            <div className="invalid-feedback">{errors?.brandId}</div>
          )}
        </FormGroup>

        <FormGroup className="mb-3">
          <Label for="countryCode">Country</Label>
          <Input
            type="select"
            name="countryCode"
            value={values?.countryCode}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.countryCode && !!errors?.countryCode}
          >
            <option value="">Select Country</option>
            {countries?.map((country, index) => (
              <option key={index} value={country?.countryCode}>
                {country?.name}
              </option>
            ))}
          </Input>
          {touched?.countryCode && errors?.countryCode && (
            <div className="invalid-feedback">{errors?.countryCode}</div>
          )}
        </FormGroup>

        {/* Added Area Field */}
        <FormGroup className="mb-3">
          <Label for="area">Area</Label>
          <Input
            type="text"
            name="area"
            value={values?.area}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.area && !!errors?.area}
          />
          {touched?.area && errors?.area && (
            <div className="invalid-feedback">{errors?.area}</div>
          )}
        </FormGroup>

        {/* Added Address Field */}
        <FormGroup className="mb-3">
          <Label for="address">Address</Label>
          <Input
            type="textarea"
            name="address"
            value={values?.address}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched?.address && !!errors?.address}
          />
          {touched?.address && errors?.address && (
            <div className="invalid-feedback">{errors?.address}</div>
          )}
        </FormGroup>

        <FormGroup className="d-flex align-items-center col-10">
          <Label for="isEcommerce" className="me-2 mb-0">
            Is it an ecommerce store?
          </Label>
          <div className="form-switch">
            <Input
              type="switch"
              name="isEcommerce"
              id="isEcommerce"
              checked={values.isEcommerce}
              onChange={handleChange}
            />
          </div>
        </FormGroup>

        {store?.storeId && (
          <FormGroup className="d-flex align-items-center col-10">
            <Label for="isDisabled" className="me-2 mb-0">
              Is Disabled
            </Label>
            <div className="form-switch">
              <Input
                type="switch"
                name="isDisabled"
                id="isDisabled"
                checked={values.isDisabled}
                onChange={handleChange}
              />
            </div>
          </FormGroup>
        )}

        <Button color="primary" type="submit" className="mt-2 bg-gradient px-5">
          {store?.storeId ? 'Save' : 'Create'}
        </Button>
      </Form>
      {loading && <InnerLoader />}
      {/* <ToastContainer /> */}
    </>
  );
};

export default CreateStoreForm;
